/**
 * State 08 — Sign up: Continue (post-OAuth).
 *
 * Lands here when an OAuth provider returns a fresh sign-up that
 * Clerk requires a username for. Email is read-only via IdentityRow;
 * the user picks a handle and submits to complete the sign-up. The
 * "Skip for now" secondary action calls `signUp.update({})` with no
 * username — Clerk will reject if username is required, in which case
 * the error banner is surfaced; otherwise the user is signed in.
 *
 * Copy verbatim from C:/tmp/mio-auth-journey.html state 08.
 */

"use client";

import { FormEvent, useState } from "react";

import { useSignUp, useClerk } from "@clerk/nextjs";

import { activateSession } from "./sessionActivation";

import { IconWarningTriangle } from "./AuthIcons";
import { EllipsisDots } from "./EllipsisDots";
import { IdentityRow } from "./IdentityRow";
import {
  AuthFlowState,
  AuthStep,
  readClerkError,
} from "./useAuthFlow";
import { trackSignupCompleted } from "@/lib/analytics";

type Props = {
  flow: AuthFlowState;
  setError: (e: string | null) => void;
  setSubmitting: (b: boolean) => void;
  goTo: (s: AuthStep) => void;
  onAuthenticated: () => void;
};

export function SignUpContinueUsernameCard({
  flow,
  setError,
  setSubmitting,
  goTo,
  onAuthenticated,
}: Props) {
  const { signUp, setActive, isLoaded } = useSignUp();
  const clerk = useClerk();
  const [username, setUsername] = useState("");

  async function complete(maybeUsername: string | undefined) {
    if (!isLoaded || !signUp || !setActive) return;
    setError(null);
    setSubmitting(true);
    try {
      const result =
        maybeUsername && maybeUsername.length > 0
          ? await signUp.update({ username: maybeUsername })
          : signUp;
      if (result.status === "complete" && result.createdSessionId) {
        // 2026-08-29: bounded activation (sessionActivation.ts).
        await activateSession({
          setActive,
          sessionId: result.createdSessionId,
        });
        // C21: through the sign-up gate. Attribution (negotiate vs
        // elsewhere) comes from the marker written when the gate was raised.
        trackSignupCompleted({ method: "sign_up" });
        // Just close the dialog; the OnboardingGate overlay (keyed on the
        // needsOnboarding flag) shows onboarding in place, no navigation.
        onAuthenticated();
        return;
      }
      setError("Could not finish sign up. Try a different username.");
    } catch (err) {
      setError(readClerkError(err));
    } finally {
      setSubmitting(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void complete(username.trim());
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <div className="mio-auth-card-header">
        <h1>Pick a username</h1>
        <p>You&rsquo;re almost in. Choose a handle for your Octopusden profile.</p>
      </div>

      {flow.error ? (
        <div className="mio-auth-error" role="alert">
          <IconWarningTriangle />
          <div>{flow.error}</div>
        </div>
      ) : null}

      <IdentityRow
        value={flow.identifier}
        onEdit={() => {
          void clerk.signOut();
          goTo("signUp.start");
        }}
      />

      <div className="mio-auth-field">
        <div className="mio-auth-field-head">
          <label className="mio-auth-field-label" htmlFor="mio-su-username">
            Username
          </label>
        </div>
        <input
          id="mio-su-username"
          className="mio-auth-input"
          type="text"
          autoComplete="username"
          autoFocus
          required
          placeholder="jane"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
      </div>

      <button
        type="submit"
        className="mio-auth-cta"
        aria-busy={flow.submitting}
        disabled={flow.submitting || username.trim().length === 0}
      >
        {flow.submitting ? (
          <>
            Saving
            <EllipsisDots />
          </>
        ) : (
          "Continue"
        )}
      </button>
      <button
        type="button"
        className="mio-auth-secondary"
        disabled={flow.submitting}
        onClick={() => void complete(undefined)}
      >
        Skip for now
      </button>
    </form>
  );
}
